/* contact.js — drag-contact geometry (cabinets stop against neighbours instead of passing
 * through). Ported from lhc-app.html: blockedIntervals (~32206), clampToContact (~32253),
 * snapToCornerRuns (~32274), snapFindPlacementAt (~32448). Units = METRES.
 */
import { WALL_THICKNESS } from "./constants";
import { getWallPlacementInfo, placementOBB } from "./placement";
import { layerCollides, lhcVOverlap } from "./collision";
import { wallInnerSpan } from "./snapping";
import { pairMinGapLR, golaChannelsFor } from "./golaClearance";
import { panelSpanOf, sidePanelExtents } from "./sidePanelClearance";

/** blockedIntervals(p, def, placements, lib, walls): absLeft ranges blocked by other
 *  same-layer, vertically-overlapping placements (linear sweep of the moving centre). (~32206) */
export function blockedIntervals(p, def, placements, lib, walls) {
  const wall = walls.find((w) => w.id === p.wallId);
  const info = wall && getWallPlacementInfo(wall, walls);
  if (!info || !def) return { intervals: [], len: 0 };
  const len = info.len;
  // FACE-AWARE PROBES (2026-07-14 peninsula fix): the sweep probes must carry
  // the mover's faceSign — without it a face −1 cabinet swept the face +1 side
  // of a double-sided wall and phantom-collided with the OTHER face's run
  // (drops slid "out of nowhere", opposite face read as occupied).
  /* ORIENTED SWEEP (batch 7, 2026-09-28): the mover's oriented box slides along the wall
   * (centre c0 + t·d, axes fixed); against each neighbour's oriented box the blocked
   * t-range is the intersection over the four separating axes of the 1-D ranges where
   * the projections overlap — exact for any wall angle (the old AABB sweep inflated both
   * boxes on angled walls, so touching neighbours read as blocked). */
  const probe0 = placementOBB({ id: p.id, type: p.type, wallId: p.wallId, absLeft: 0, config: p.config, faceSign: p.faceSign }, def, walls);
  const probe1 = placementOBB({ id: p.id, type: p.type, wallId: p.wallId, absLeft: 1, config: p.config, faceSign: p.faceSign }, def, walls);
  if (!probe0 || !probe1) return { intervals: [], len };
  const c0x = probe0.cx, c0z = probe0.cz;
  const dCx = probe1.cx - c0x, dCz = probe1.cz - c0z;
  const iv = [];
  placements.forEach((q) => {
    if (q === p || q.id === p.id) return;
    const qd = lib[q.type];
    if (!qd || !layerCollides(def.role, qd.role)) return;
    if (!lhcVOverlap(p, q, lib)) return;
    const qb = placementOBB(q, qd, walls);
    if (!qb) return;
    let lo = -Infinity, hi = Infinity, sep = false;
    for (const [nx, nz] of [[probe0.ux, probe0.uz], [probe0.vx, probe0.vz], [qb.ux, qb.uz], [qb.vx, qb.vz]]) {
      const hm = Math.abs(probe0.ux * nx + probe0.uz * nz) * probe0.hw + Math.abs(probe0.vx * nx + probe0.vz * nz) * probe0.hd;
      const hq = Math.abs(qb.ux * nx + qb.uz * nz) * qb.hw + Math.abs(qb.vx * nx + qb.vz * nz) * qb.hd;
      const lim = hm + hq;
      const pm0 = c0x * nx + c0z * nz, dn = dCx * nx + dCz * nz, pq = qb.cx * nx + qb.cz * nz;
      if (Math.abs(dn) < 1e-9) { if (Math.abs(pm0 - pq) >= lim) { sep = true; break; } continue; }
      const a = (pq - lim - pm0) / dn, b = (pq + lim - pm0) / dn;
      lo = Math.max(lo, Math.min(a, b)); hi = Math.min(hi, Math.max(a, b));
    }
    if (!sep && hi > lo) iv.push([lo, hi]);
    /* GOLA CLEARANCE (2026-08-22 mandate: the vertical profile is PHYSICAL —
     * a hard 30mm keep-out on its side). Same-wall, same-face neighbours add
     * a direct absLeft interval with the pair gap on the gola side(s); zero
     * clearance pushes nothing, so every flush behaviour stays byte-identical. */
    if ((q.wallId || null) === (p.wallId || null)) {
      const dblW = wall.style === "partition" || wall.style === "peninsula";
      const fs = (x) => (x.faceSign === -1 ? -1 : 1);
      if (!dblW || fs(p) === fs(q)) {
        const cL = pairMinGapLR(p, def, q, qd);   // p standing LEFT of q
        const cR = pairMinGapLR(q, qd, p, def);   // p standing RIGHT of q
        if (cL > 0 || cR > 0) iv.push([(q.absLeft || 0) - def.width - cL, (q.absLeft || 0) + qd.width + cR]);
      }
    }
  });
  // GOLA CHANNEL (2026-08-30): an extended post is a 30mm channel over its
  // full height — every module whose band it passes is clamped out of it
  golaChannelsFor(p, def, placements, (x) => lib[x.type]).forEach((ch) => {
    iv.push([ch.x0 - def.width, ch.x1]);
  });
  iv.sort((A, B) => A[0] - B[0]);
  /* SEAMS ARE LEGAL (batch 7, 2026-09-28): two neighbours whose blocked ranges only
   * TOUCH (a gap that fits the mover exactly) used to merge into one block that swallowed
   * the single legal position between them — the drag was pushed out to the far side.
   * Ranges merge only when they truly overlap (by more than CONTACT_SEAM_M). */
  const merged = [];
  iv.forEach((x) => {
    if (merged.length && x[0] < merged[merged.length - 1][1] - CONTACT_SEAM_M) merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], x[1]);
    else merged.push([x[0], x[1]]);
  });
  return { intervals: merged, len };
}

/** A seam narrower than this (0.1 mm) between two blocked ranges is a legal position. */
export const CONTACT_SEAM_M = 1e-4;

/** clampToContact(p, def, desired, fromLeft, placements, lib, walls): stop the drag dead
 *  at the contacting face instead of passing through a neighbour. (~32253) */
export function clampToContact(p, def, desired, fromLeft, placements, lib, walls) {
  const B = blockedIntervals(p, def, placements, lib, walls);
  if (!B.len) return desired;
  // Signed span bounds, NOT a 0 floor: 0 is the centerline node — at a convex corner
  // the legal minimum is off0 = −t/2 (flush with the visible band corner).
  const wall = walls.find((w) => w.id === p.wallId);
  const sp = wall ? wallInnerSpan(wall, walls) : { off0: 0, off1: 0 };
  /* SIDE PANELS (2026-09-07): the clamp stops the module's REAL faces at the
   * wall span — a board may not hang past the wall end or into the return. */
  const _psp = panelSpanOf(p, def);
  const minLeft = sp.off0 - _psp.lo;
  const maxLeft = B.len - sp.off1 - _psp.hi;
  let t = Math.max(minLeft, Math.min(desired, maxLeft));
  const inBlock = (iv) => t > iv[0] + CONTACT_SEAM_M && t < iv[1] - CONTACT_SEAM_M;
  /* push out of every range the position lands in (a few passes: leaving one range can
   * land on the edge of the next), always in the direction the drag came from */
  for (let pass = 0; pass < 6; pass++) {
    const hit = B.intervals.find(inBlock);
    if (!hit) break;
    if (typeof fromLeft === "number" && isFinite(fromLeft)) t = fromLeft <= hit[0] + CONTACT_SEAM_M ? hit[0] : hit[1];
    else t = t - hit[0] < hit[1] - t ? hit[0] : hit[1];
    t = Math.max(minLeft, Math.min(t, maxLeft));
  }
  for (const iv of B.intervals) if (inBlock(iv)) return typeof fromLeft === "number" ? fromLeft : desired;
  return t;
}

/** snapExactFit(p, def, desired, placements, lib, walls, tolM):
 *  TIGHT-GAP AUTO-SEAT (2026-07-14 CAD mandate, Issue 4): when the cabinet's
 *  desired position overlaps a free gap that fits it almost exactly
 *  (gapLen − width ≤ tolM), there is essentially ONE legal position — snap
 *  straight into it (flush to the gap start) instead of demanding pixel-perfect
 *  dragging. 300 cabinet into a 300 gap: instant. 300 into 301: instant, flush.
 *  Larger gaps are untouched (normal magnets apply). Returns absLeft. */
export function snapExactFit(p, def, desired, placements, lib, walls, tolM = 0.004) {
  return snapExactFitEx(p, def, desired, placements, lib, walls, tolM).absLeft;
}

/** snapExactFitEx: the seat AND whether a tight opening was matched — callers that
 *  compared `seated !== desired` lost the seat whenever the drag already sat exactly on
 *  it (the contact clamp then pushed it out: "sometimes it doesn't fit"). */
export function snapExactFitEx(p, def, desired, placements, lib, walls, tolM = 0.004) {
  if (!def) return { absLeft: desired, seated: false };
  const wall = walls.find((w) => w.id === p.wallId);
  const info = wall && getWallPlacementInfo(wall, walls);
  if (!info) return { absLeft: desired, seated: false };
  // PHYSICAL neighbour spans — NOT blockedIntervals: for an exact-fit opening
  // the two neighbours' blocked intervals MERGE and swallow the one legal
  // position (that's exactly why placement needed pixel-perfect dragging).
  const sp = wallInnerSpan(wall, walls);
  const lo0 = sp.off0, end = info.len - sp.off1;
  // FACE FILTER (2026-07-14 peninsula fix): only same-face units bound a gap.
  const dbl = wall.style === "partition" || wall.style === "peninsula";
  const fsOf = (q) => (q.faceSign === -1 ? -1 : 1);
  const spans = [];
  (placements || []).forEach((q) => {
    if (!q || q === p || q.id === p.id || (q.wallId || null) !== (p.wallId || null)) return;
    if (dbl && fsOf(q) !== fsOf(p)) return;
    const qd = lib[q.type];
    if (!qd || !layerCollides(def.role, qd.role)) return;
    if (!lhcVOverlap(p, q, lib)) return;
    // GOLA CLEARANCE: the profile's 30mm keep-out shrinks the free gap
    // the neighbour's own panels are occupied space for the free-gap math
    const _qs = panelSpanOf(q, qd);
    spans.push([
      q.absLeft + _qs.lo - pairMinGapLR(p, def, q, qd),
      q.absLeft + _qs.hi + pairMinGapLR(q, qd, p, def),
    ]);
  });
  // GOLA CHANNEL: the 30mm channel is occupied space for the free-gap math
  golaChannelsFor(p, def, placements, (x) => lib[x.type]).forEach((ch) => spans.push([ch.x0, ch.x1]));
  spans.sort((a, b) => a[0] - b[0]);
  const gaps = [];
  let cursor = lo0;
  spans.forEach(([a, b]) => {
    if (a > cursor + 1e-9) gaps.push([cursor, a]);
    cursor = Math.max(cursor, b);
  });
  if (end > cursor + 1e-9) gaps.push([cursor, end]);
  const _ps = panelSpanOf(p, def);
  const _pw = _ps.hi - _ps.lo;                      // the module INCLUDING its own boards
  const minLeft = sp.off0 - _ps.lo, maxLeft = info.len - sp.off1 - _ps.hi;
  for (const [ga, gb] of gaps) {
    const slack = (gb - ga) - _pw;
    if (slack < -1e-9 || slack > tolM) continue;    // doesn't fit / not tight
    // the drag is "at" this opening when the cabinet's interval overlaps it
    if (desired + _ps.hi > ga - 0.02 && desired + _ps.lo < gb + 0.02) {
      return { absLeft: Math.max(minLeft, Math.min(ga - _ps.lo, maxLeft)), seated: true };
    }
  }
  return { absLeft: desired, seated: false };
}

/** snapToCornerRuns(p, def, snapDistM, placements, lib, walls): snap absLeft to a blocked
 *  interval edge (flush against the run on the adjacent wall). Returns new absLeft. (~32274) */
export function snapToCornerRuns(p, def, snapDistM, placements, lib, walls) {
  const B = blockedIntervals(p, def, placements, lib, walls);
  // Edge acceptance uses the SIGNED span (off0 may be negative at a convex corner) —
  // the old `edge < -1e-6` floor rejected legal flush-past-the-node positions.
  const wallCR = walls.find((w) => w.id === p.wallId);
  const spCR = wallCR ? wallInnerSpan(wallCR, walls) : { off0: 0, off1: 0 };
  let best = null, bestD = snapDistM;
  B.intervals.forEach((iv) => {
    [iv[0], iv[1]].forEach((edge) => {
      if (edge < spCR.off0 - 1e-6 || edge > B.len - spCR.off1 - def.width + 1e-6) return;
      const d = Math.abs(p.absLeft - edge);
      if (d > 1e-6 && d <= bestD) { bestD = d; best = edge; }
    });
  });
  return best !== null ? best : p.absLeft;
}

/** snapFindPlacementAt(wx, wz, placements, lib, walls): which placement is under a world
 *  point (drag pickup hit-test). (~32448) */
export function snapFindPlacementAt(wx, wz, placements, lib, walls) {
  for (let i = placements.length - 1; i >= 0; i--) {
    const p = placements[i];
    const def = lib[p.type];
    if (!def) continue;
    const wall = p.wallId ? walls.find((w) => w.id === p.wallId) : null;
    if (!wall) continue;
    const info = getWallPlacementInfo(wall, walls);
    if (!info) continue;
    const cx = wall.start[0] + info.dirX * (p.absLeft + def.width / 2) + info.nx * (WALL_THICKNESS / 2 + def.depth / 2);
    const cz = wall.start[1] + info.dirZ * (p.absLeft + def.width / 2) + info.nz * (WALL_THICKNESS / 2 + def.depth / 2);
    const dx = wx - cx, dz = wz - cz;
    const localAlong = dx * info.dirX + dz * info.dirZ;
    const localPerp = dx * info.nx + dz * info.nz;
    // a click on the module's own side panel picks the module up too
    const e = sidePanelExtents(p, def);
    if (localAlong >= -def.width / 2 - e.left - 0.01 && localAlong <= def.width / 2 + e.right + 0.01
      && Math.abs(localPerp) <= def.depth / 2 + 0.01) return p;
  }
  return null;
}
